<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  COMPONENT_TYPES,
  MEDIA_FORMATS,
  findComponentByType,
} from 'dashboard/helper/templateHelper';

const props = defineProps({
  templates: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['select']);
const { t } = useI18n();
const query = ref('');

const filteredTemplates = computed(() => {
  const normalizedQuery = query.value.trim().toLowerCase();
  if (!normalizedQuery) return props.templates;
  return props.templates.filter(template =>
    template.name.toLowerCase().includes(normalizedQuery)
  );
});

const component = (template, type) => findComponentByType(template, type);

const hasMediaHeader = template =>
  MEDIA_FORMATS.includes(component(template, COMPONENT_TYPES.HEADER)?.format);
</script>

<template>
  <div class="w-full">
    <div
      class="mb-3 flex h-10 items-center gap-2 rounded-lg border border-n-weak bg-n-alpha-1 px-3 focus-within:border-n-brand"
    >
      <fluent-icon icon="search" class="text-n-slate-11" size="16" />
      <input
        v-model="query"
        type="search"
        :placeholder="t('CHANNEL_TEMPLATE_PRESETS.PICKER.SEARCH')"
        class="reset-base h-full w-full bg-transparent text-sm text-n-slate-12 outline-none"
      />
    </div>

    <div
      class="max-h-[26rem] overflow-y-auto rounded-lg border border-n-weak bg-n-background p-2"
    >
      <button
        v-for="template in filteredTemplates"
        :key="`${template.id || template.name}-${template.language}`"
        type="button"
        class="block w-full rounded-lg p-3 text-left hover:bg-n-alpha-2"
        @click="emit('select', template)"
      >
        <div class="flex items-start justify-between gap-3">
          <p class="text-sm font-medium text-n-slate-12">
            {{ template.name }}
          </p>
          <span
            class="shrink-0 rounded-md bg-n-slate-3 px-2 py-1 text-xs text-n-slate-12"
          >
            {{ template.language }}
          </span>
        </div>
        <p
          v-if="component(template, COMPONENT_TYPES.BODY)?.text"
          class="mt-2 line-clamp-3 whitespace-pre-wrap text-sm text-n-slate-11"
        >
          {{ component(template, COMPONENT_TYPES.BODY).text }}
        </p>
        <div class="mt-2 flex flex-wrap gap-2 text-xs text-n-slate-11">
          <span>{{ template.category }}</span>
          <span v-if="hasMediaHeader(template)">
            {{
              t('CHANNEL_TEMPLATE_PRESETS.PICKER.MEDIA', {
                format: component(template, COMPONENT_TYPES.HEADER).format,
              })
            }}
          </span>
        </div>
      </button>

      <p
        v-if="!filteredTemplates.length"
        class="px-4 py-10 text-center text-sm text-n-slate-11"
      >
        {{
          query
            ? t('CHANNEL_TEMPLATE_PRESETS.PICKER.NO_RESULTS')
            : t('CHANNEL_TEMPLATE_PRESETS.EMPTY')
        }}
      </p>
    </div>
  </div>
</template>
